import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CircleCheck } from 'lucide-react';

import { cn } from '@/lib/utils';

// Name must match the channel listened to in BillingPage/Settings.
const CHECKOUT_CHANNEL = 'optidev-checkout';

type CheckoutType = 'top-up' | 'cloud-add-funds';

const REDIRECT_URLS: Record<CheckoutType, string> = {
  'top-up': '/dashboard/settings/billing?top_up=success',
  'cloud-add-funds': '/dashboard/settings/cloud-usage?cloud_add_funds=success',
};

export default function CheckoutSuccess(): JSX.Element {
  const [searchParams] = useSearchParams();
  // Embed opens via window.open → has opener → window.close() succeeds.
  // Standalone navigates in same tab → no opener → redirect in place.
  const isPopup = typeof window !== 'undefined' && window.opener !== null;
  // Guards against React StrictMode's double-invoked effects — broadcast must fire once.
  const hasBroadcastRef = useRef(false);

  useEffect(() => {
    const type = searchParams.get('type') as CheckoutType | null;

    // Broadcast so the still-mounted billing/cloud-usage page can refresh balances and toast.
    if (!hasBroadcastRef.current && type && typeof BroadcastChannel !== 'undefined') {
      hasBroadcastRef.current = true;
      const channel = new BroadcastChannel(CHECKOUT_CHANNEL);
      channel.postMessage({ type });
      channel.close();
    }

    if (isPopup) {
      window.close();
    } else {
      window.location.replace((type && REDIRECT_URLS[type]) || '/dashboard/settings/billing');
    }
  }, [isPopup, searchParams]);

  const subtitle = isPopup ? 'You can close this tab.' : 'Redirecting…';

  return (
    <div className={cn('min-h-screen flex items-center justify-center bg-background px-4')}>
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/10">
          <CircleCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h1 className="text-base font-semibold">Payment successful</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
